// Single source for plan prices, taglines and feature copy, shared by the sign-up plan
// selection screen and Plan & billing. Change prices here only.
//
// Prices are final (confirmed 2026-10-10). No GST is charged, so these are the full amounts
// paid and no "incl. GST" wording is shown. If GST registration changes, revisit the prices
// and the price labels in PlanCard.

export const TRIAL_DAYS = 14;
export const CURRENCY = 'AUD';

export const PLAN_IDS = ['solo', 'family'];
export const BILLING_INTERVALS = ['monthly', 'yearly'];

export const PLAN_PRICES = {
  solo: { monthly: 6.99, yearly: 59.99 },
  family: { monthly: 9.99, yearly: 89.99 },
};

// Copy rules (spec §5): no "AI" wording, no dietary preferences, no cooking duties, and
// household members do not add recipes. The mockup PNGs show OLD wording — these lists win.
// ONE feature list per plan, shown in full on both sign-up and Plan & billing (decided
// 2026-10-11), so the two screens can never describe a plan differently. Same for the tagline.
export const PLAN_DETAILS = {
  solo: {
    name: 'Solo',
    tagline: 'Perfect for individuals',
    features: [
      'Unlimited recipes, imported from a link, photo or file',
      'One-tap weekly meal plans from your own recipes',
      'Shopping list that updates itself',
      'Your recipes, your rules',
      // Confirmed 2026-10-11: pantry staples are left off the shopping list.
      'Your week, your way: serves per meal, pantry staples, any start day',
      'Skip and swap meals easily',
      'Cook mode keeps your screen on',
    ],
  },
  family: {
    name: 'Family',
    tagline: 'Best for households',
    features: [
      'Everything in Solo',
      'Up to 4 household members',
      'Shared recipes and shopping list',
      'Everyone sees the plan, so anyone can cook or do the shop',
    ],
  },
};

export function formatPrice(amount) {
  return `$${amount.toFixed(2)}`;
}

// "A$6.99": the dollar sign marked as Australian, for places that don't say "AUD" nearby.
export function formatAudPrice(amount) {
  return `A${formatPrice(amount)}`;
}

export function getPlanPrice(plan, interval) {
  return PLAN_PRICES[plan]?.[interval] ?? null;
}

// Yearly price spread over 12 months, e.g. 53.99 -> 4.50.
export function getMonthlyEquivalent(plan) {
  const yearly = getPlanPrice(plan, 'yearly');
  return yearly == null ? null : Math.round((yearly / 12) * 100) / 100;
}

// The "Save N%" label, derived from the prices so it can't drift when they change. Uses the
// smallest saving across plans (rounded) so the label holds for both. Current prices: 28.5%
// Solo, 24.9% Family -> "Save 25%".
export function getYearlySavingsPercent() {
  const savings = PLAN_IDS.map((plan) => {
    const { monthly, yearly } = PLAN_PRICES[plan];
    return 1 - yearly / (monthly * 12);
  });
  return Math.round(Math.min(...savings) * 100);
}

// When a trial that starts now ends, i.e. the first charge date for someone who subscribes at
// sign-up (the 14 free days still apply). Display only: the backend/Stripe date is authoritative.
export function getTrialEndDate(from = new Date()) {
  const end = new Date(from);
  end.setDate(end.getDate() + TRIAL_DAYS);
  return end;
}

// Billing dates as in the mockups: "Mon 19 Oct", with the year added only when it isn't the
// current one ("Mon 19 Oct 2027"), so a yearly renewal date is never ambiguous.
export function formatBillingDate(date) {
  // No date means no text. new Date(null) is 1 Jan 1970, which once showed as "Renews on Thu 1 Jan
  // 1970" for plans set by hand (they have no renewal date until Stripe supplies one).
  if (date == null || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const withYear = d.getFullYear() !== new Date().getFullYear();
  const parts = new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return [get('weekday'), get('day'), get('month'), withYear ? get('year') : '']
    .filter(Boolean)
    // Non-breaking spaces keep the date on one line when the sentence wraps.
    .join('\u00A0');
}

// Days left in a trial, counted in calendar days on the person's own clock: Mon 19 Oct to
// Mon 2 Nov is 14, whatever the time of day. Counting elapsed time and rounding up said 15
// whenever the end fell a few minutes past the same time of day, which it does: the server rounds
// the trial end up to its 10-minute idempotency window. 0 means it ends today.
export function trialDaysLeft(endDate, now = new Date()) {
  if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) return null;
  const dayNumber = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000;
  return dayNumber(endDate) - dayNumber(now);
}
